import { useState } from "react";
import { useTranslation } from "react-i18next";
import { denyAnalytics, grantAnalytics, storedConsent } from "@/lib/analytics";

/**
 * One-line analytics consent, shown once until answered.
 *
 * Deliberately not a modal and not a dark pattern: both choices are the same
 * size and the same weight, which is what makes the "accept" a valid consent
 * rather than a cornered one. Nothing is measured until this is answered — the
 * tag boots with `analytics_storage: denied`.
 */
export function AnalyticsConsent() {
  const { t } = useTranslation();
  const [answered, setAnswered] = useState(() => storedConsent() !== null);

  if (answered) return null;

  const decide = (accept: boolean) => {
    if (accept) grantAnalytics();
    else denyAnalytics();
    setAnswered(true);
  };

  return (
    <div
      role="region"
      aria-label={t("analytics.aria", "Analytics preference")}
      className="fixed inset-x-0 bottom-0 z-40 border-t border-cream-border bg-cream-light px-4 py-3 shadow-soft"
    >
      <div className="mx-auto flex max-w-3xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-[13px] leading-snug text-taupe">
          {t(
            "analytics.body",
            "We'd like to measure which screens get used, so the next thing we build is the thing you actually open. No advertising, no selling data.",
          )}
        </p>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={() => decide(false)}
            className="rounded border border-cream-border px-3 py-1.5 text-[13px] font-medium text-navy hover:border-navy focus:outline-none focus:ring-2 focus:ring-amber/45"
          >
            {t("analytics.decline", "No thanks")}
          </button>
          <button
            type="button"
            onClick={() => decide(true)}
            className="rounded border border-navy bg-navy px-3 py-1.5 text-[13px] font-medium text-cream-light hover:bg-navy-soft focus:outline-none focus:ring-2 focus:ring-amber/45"
          >
            {t("analytics.accept", "Allow")}
          </button>
        </div>
      </div>
    </div>
  );
}
